import { useI18n } from '@/i18n'
import { CaretDown } from '@/shared/icons/phosphor'
import {
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  onCleanup,
  onMount,
} from 'solid-js'
import { Button } from '../Button'
import './ExpandableContent.css'

const DEFAULT_COLLAPSED_HEIGHT = 240
const MIN_HIDDEN_HEIGHT = 120
const STRANDED_HEADING_ZONE = 72
const LINE_BREAK_SEARCH = 48

export interface ExpandableContentProps {
  children: JSX.Element
  collapsedHeight?: number
  contentTheme?: 'light'
  class?: string
}

const lineBottomsOf = (root: HTMLElement, origin: number) => {
  const range = document.createRange()
  range.selectNodeContents(root)
  if (typeof range.getClientRects !== 'function') {
    return []
  }
  return Array.from(range.getClientRects(), (rect) => rect.bottom - origin)
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function ExpandableContent(props: ExpandableContentProps) {
  const { t } = useI18n()
  const viewportId = createUniqueId()
  const [contentHeight, setContentHeight] = createSignal(0)
  const [headingTops, setHeadingTops] = createSignal<number[]>([])
  const [lineBottoms, setLineBottoms] = createSignal<number[]>([])
  const [expanded, setExpanded] = createSignal(false)
  const [animated, setAnimated] = createSignal(false)
  const [dockEdge, setDockEdge] = createSignal<number>()
  let root: HTMLDivElement | undefined
  let viewport: HTMLDivElement | undefined
  let content: HTMLDivElement | undefined
  let dock: HTMLDivElement | undefined

  const collapsedHeight = () => props.collapsedHeight ?? DEFAULT_COLLAPSED_HEIGHT
  const collapsible = () => contentHeight() > collapsedHeight() + MIN_HIDDEN_HEIGHT
  const collapsed = () => collapsible() && !expanded()
  const previewHeight = createMemo(() => {
    const limit = collapsedHeight()
    const end =
      headingTops().find((top) => top > limit - STRANDED_HEADING_ZONE && top < limit) ?? limit
    const lineBreaks = lineBottoms().filter(
      (bottom) => bottom <= end && bottom > end - LINE_BREAK_SEARCH
    )
    return lineBreaks.length > 0 ? Math.floor(Math.max(...lineBreaks)) : end
  })

  onMount(() => {
    const measured = content
    if (!measured) {
      return
    }
    const measure = () => {
      const origin = measured.getBoundingClientRect().top
      setHeadingTops(
        Array.from(
          measured.querySelectorAll('h1, h2, h3, h4, h5, h6'),
          (heading) => heading.getBoundingClientRect().top - origin
        )
      )
      setLineBottoms(lineBottomsOf(measured, origin))
      setContentHeight(measured.offsetHeight)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(measured)
    onCleanup(() => observer.disconnect())
  })

  const trackDock = () => {
    if (!viewport || !dock) {
      return
    }
    const text = viewport.getBoundingClientRect()
    const edge = dock.getBoundingClientRect().top
    setDockEdge(edge < text.bottom - 1 ? Math.max(0, Math.round(edge - text.top)) : undefined)
  }

  createEffect(() => {
    if (!expanded() || !collapsible()) {
      setDockEdge(undefined)
      return
    }
    trackDock()
    document.addEventListener('scroll', trackDock, { capture: true, passive: true })
    window.addEventListener('resize', trackDock)
    const resized =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(trackDock)
    if (viewport) {
      resized?.observe(viewport)
    }
    onCleanup(() => {
      document.removeEventListener('scroll', trackDock, { capture: true })
      window.removeEventListener('resize', trackDock)
      resized?.disconnect()
    })
  })

  const expand = () => {
    setAnimated(true)
    setExpanded(true)
  }

  const collapse = () => {
    setAnimated(false)
    setExpanded(false)
    root?.scrollIntoView({
      block: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })
  }

  const revealFocusedContent = (event: FocusEvent) => {
    if (!collapsed()) {
      return
    }
    setAnimated(false)
    setExpanded(true)
    if (event.target instanceof HTMLElement) {
      event.target.scrollIntoView({ block: 'nearest' })
    }
  }

  const dockEdgeStyle = () => (dockEdge() === undefined ? undefined : `${dockEdge()}px`)

  const viewportElement = (
    <div
      ref={viewport}
      id={viewportId}
      class="ds-expandable__viewport"
      classList={{
        'ds-expandable__viewport--collapsed': collapsed(),
        'ds-expandable__viewport--animated': animated(),
        'ds-expandable__viewport--docked': dockEdge() !== undefined,
      }}
      style={{
        '--ds-expandable-collapsed-height': `${previewHeight()}px`,
        '--ds-expandable-dock-edge': dockEdgeStyle(),
      }}
      onFocusIn={revealFocusedContent}
    >
      <div ref={content} class="ds-expandable__content">
        {props.children}
      </div>
    </div>
  )

  return (
    <div ref={root} class={['ds-expandable', props.class].filter(Boolean).join(' ')}>
      <Show when={props.contentTheme} fallback={viewportElement}>
        {(theme) => (
          <div
            data-theme={theme()}
            class="ds-expandable__surface rounded-[var(--radius-md)] bg-[var(--color-surface)]"
            classList={{ 'ds-expandable__surface--docked': dockEdge() !== undefined }}
            style={{ '--ds-expandable-dock-edge': dockEdgeStyle() }}
          >
            {viewportElement}
          </div>
        )}
      </Show>
      <Show when={collapsible()}>
        <div
          ref={dock}
          class="ds-expandable__dock"
          classList={{ 'ds-expandable__dock--pinned': expanded() }}
        >
          <Button
            type="button"
            variant="link"
            size="sm"
            class="ds-expandable__toggle"
            aria-expanded={expanded()}
            aria-controls={viewportId}
            onClick={() => (expanded() ? collapse() : expand())}
          >
            {expanded()
              ? t('designSystem.expandableContent.readLess')
              : t('designSystem.expandableContent.readMore')}
            <CaretDown aria-hidden="true" weight="bold" class="ds-expandable__caret" />
          </Button>
        </div>
      </Show>
    </div>
  )
}
